/**
 * Página pública de agendamento online - Design Premium
 * 
 * Permite que visitantes (sem autenticação) agendem um horário
 * selecionando data, horário e preenchendo seus dados.
 * 
 * Fluxo: Slug → Event Type → Data → Horário → Formulário → Confirmação
 * Suporta ?deal_id=UUID para pré-preencher dados do contato
 * Detecta booking existente para deal_id e exibe confirmação com opções
 * 
 * RELEASE: 2026-02-14
 * VERSION: 3.0.0 - Suporte a detecção de booking existente + cancelar/reagendar
 */

import { useState, useEffect } from 'react';
import { stripTemplateVariables } from '@/lib/schedulingUtils';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePublicEventType, usePublicDealContext, useExistingDealBooking } from '@/hooks/usePublicScheduling';
import { PublicSchedulingDatePicker } from '@/components/public/scheduling/PublicSchedulingDatePicker';
import { PublicSchedulingSlots } from '@/components/public/scheduling/PublicSchedulingSlots';
import { PublicSchedulingForm } from '@/components/public/scheduling/PublicSchedulingForm';
import { PublicSchedulingConfirmation } from '@/components/public/scheduling/PublicSchedulingConfirmation';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarClock, Clock } from 'lucide-react';
import GoogleMeetIcon from '@/assets/google-meet-icon.svg';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

const logger = createLogger('PublicScheduling');

/** Estados possíveis do fluxo de agendamento */
type BookingStep = 'select-date' | 'select-time' | 'fill-form' | 'confirmed';

/** Etapas visíveis no stepper */
const STEPPER_STEPS = ['Data', 'Horário', 'Dados'];

/** Mapeia step para índice do stepper */
const getStepIndex = (step: BookingStep): number => {
  switch (step) {
    case 'select-date': return 0;
    case 'select-time': return 1;
    case 'fill-form': return 2;
    case 'confirmed': return 3;
  }
};

const PublicScheduling = () => {
  const { tenantSlug, slug } = useParams<{ tenantSlug: string; slug: string }>();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('deal_id');
  
  // Busca event type filtrando pelo slug do tenant para garantir unicidade
  const { data: eventType, isLoading, error } = usePublicEventType(slug, tenantSlug);
  
  // Buscar contexto do deal quando deal_id presente na URL
  const { data: dealContext, isLoading: isDealContextLoading } = usePublicDealContext(dealId);

  // Verificar se já existe booking ativo para este deal + event_type
  const { 
    data: existingBooking, 
    isLoading: isExistingBookingLoading,
    refetch: refetchExistingBooking,
  } = useExistingDealBooking(dealId, eventType?.id);

  // Estado do fluxo
  const [step, setStep] = useState<BookingStep>('select-date');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  // Token de cancelamento para booking existente via deal
  const [activeCancelToken, setActiveCancelToken] = useState<string | null>(null);
  // Flag para evitar redirecionamento automático após reagendar/cancelar
  const [skipExistingCheck, setSkipExistingCheck] = useState(false);

  // Efeito: quando detecta booking existente, pula direto para confirmação
  // Ignorado quando skipExistingCheck está ativo (após reagendar/cancelar)
  useEffect(() => {
    if (existingBooking && step === 'select-date' && !skipExistingCheck) {
      logger.info('Booking existente detectado para deal, exibindo confirmação', {
        bookingId: existingBooking.id,
      });
      setBookingId(existingBooking.id);
      setSelectedSlot(existingBooking.start_at);
      setActiveCancelToken(existingBooking.cancel_token);
      setStep('confirmed');
    }
  }, [existingBooking, step, skipExistingCheck]);

  /** Quando uma data é selecionada */
  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep('select-time');
  };

  /** Quando um horário é selecionado */
  const handleSlotSelect = (slotStart: string) => {
    setSelectedSlot(slotStart);
    setStep('fill-form');
  };

  /** Quando o booking é confirmado - captura cancel_token via refetch */
  const handleBookingConfirmed = async (id: string) => {
    setBookingId(id);
    setSkipExistingCheck(false);

    // Se tem deal_id, buscar cancel_token do booking recém-criado
    if (dealId) {
      try {
        const result = await refetchExistingBooking();
        if (result.data?.cancel_token) {
          setActiveCancelToken(result.data.cancel_token);
        }
      } catch (err) {
        logger.warn('Não foi possível obter cancel_token do novo booking', err);
      }
    }

    setStep('confirmed');
  };

  /** Volta para a etapa anterior */
  const handleBack = () => {
    if (step === 'fill-form') {
      setStep('select-time');
      setSelectedSlot(null);
    } else if (step === 'select-time') {
      setStep('select-date');
      setSelectedDate(null);
    }
  };

  /** Handler: Reagendar - ativa flag para evitar redirecionamento automático */
  const handleReschedule = () => {
    logger.info('Reagendamento solicitado, voltando ao fluxo de seleção');
    setSkipExistingCheck(true);
    setBookingId(null);
    setSelectedSlot(null);
    setSelectedDate(null);
    setActiveCancelToken(null);
    setStep('select-date');
  };

  /** Handler: Cancelamento confirmado - ativa flag para navegação livre */
  const handleCancelConfirmed = () => {
    logger.info('Booking cancelado pelo visitante');
    setSkipExistingCheck(true);
  };

  // Estado de carregamento (inclui deal context e booking existente)
  if (isLoading || (dealId && isDealContextLoading) || (dealId && eventType && isExistingBookingLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background flex items-center justify-center p-4">
        <div className="w-full max-w-2xl space-y-6">
          <Skeleton className="h-16 w-16 rounded-full mx-auto" />
          <Skeleton className="h-8 w-64 mx-auto" />
          <Skeleton className="h-4 w-96 mx-auto" />
          <Skeleton className="h-[400px] w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // Evento não encontrado
  if (!eventType || error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted text-muted-foreground mx-auto shadow-sm">
            <CalendarClock className="h-9 w-9" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            Agendamento não encontrado
          </h1>
          <p className="text-muted-foreground max-w-sm">
            Este link de agendamento não está disponível ou foi desativado.
          </p>
        </div>
      </div>
    );
  }

  /** Cor do evento com fallback */
  const eventColor = eventType.color || '#3B82F6';
  const currentStepIndex = getStepIndex(step);

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
        
        {/* Header premium do evento */}
        <div className="text-center mb-8 overflow-hidden px-2">
          {/* Logo ou ícone decorativo */}
          {eventType.logo_url ? (
            <img
              src={eventType.logo_url}
              alt={eventType.title}
              className="h-16 w-16 rounded-full object-cover ring-2 ring-border shadow-md mx-auto mb-4"
            />
          ) : (
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md ring-1 ring-border/50"
              style={{ backgroundColor: `${eventColor}12` }}
            >
              <CalendarClock className="h-7 w-7" style={{ color: eventColor }} />
            </div>
          )}

          {/* Título */}
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            {stripTemplateVariables(eventType.title)}
          </h1>

          {/* Descrição */}
          {eventType.description && (
            <p className="text-muted-foreground mt-2 max-w-lg mx-auto text-sm leading-relaxed break-words overflow-hidden">
              {eventType.description}
            </p>
          )}

          {/* Badges informativos com estilo pill premium */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border"
              style={{
                backgroundColor: `${eventColor}08`,
                borderColor: `${eventColor}25`,
                color: eventColor,
              }}
            >
              <Clock className="h-3.5 w-3.5" />
              {eventType.duration_minutes} min
            </span>
            {eventType.conferencing_provider === 'google_meet' && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border/50 bg-muted/50 text-muted-foreground">
                <img src={GoogleMeetIcon} alt="Google Meet" className="h-3.5 w-3.5" />
                Google Meet
              </span>
            )}
          </div>
        </div>

        {/* Stepper de progresso (oculto na tela de confirmação) */}
        {step !== 'confirmed' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {STEPPER_STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div
                    className={cn(
                      "h-2 w-2 rounded-full transition-all duration-300",
                      i <= currentStepIndex ? "scale-110" : "bg-muted-foreground/20"
                    )}
                    style={i <= currentStepIndex ? { backgroundColor: eventColor } : undefined}
                  />
                  <span
                    className={cn(
                      "text-xs font-medium transition-colors duration-300",
                      i <= currentStepIndex ? "text-foreground" : "text-muted-foreground/40"
                    )}
                  >
                    {label}
                  </span>
                </div>
                {i < STEPPER_STEPS.length - 1 && (
                  <div
                    className={cn(
                      "w-8 h-px transition-colors duration-300",
                      i < currentStepIndex ? "opacity-60" : "bg-border"
                    )}
                    style={i < currentStepIndex ? { backgroundColor: eventColor } : undefined}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Card principal premium */}
        <div className="bg-card border border-border/40 rounded-2xl shadow-lg overflow-hidden">
          {step === 'select-date' && (
            <PublicSchedulingDatePicker
              eventType={eventType}
              onDateSelect={handleDateSelect}
            />
          )}

          {step === 'select-time' && selectedDate && (
            <PublicSchedulingSlots
              eventType={eventType}
              selectedDate={selectedDate}
              onSlotSelect={handleSlotSelect}
              onBack={handleBack}
            />
          )}

          {step === 'fill-form' && selectedSlot && (
            <PublicSchedulingForm
              eventType={eventType}
              selectedSlot={selectedSlot}
              onConfirmed={handleBookingConfirmed}
              onBack={handleBack}
              dealId={dealId || undefined}
              prefillData={dealContext || undefined}
            />
          )}

          {step === 'confirmed' && selectedSlot && bookingId && (
            <PublicSchedulingConfirmation
              eventType={eventType}
              selectedSlot={selectedSlot}
              bookingId={bookingId}
              cancelToken={activeCancelToken || undefined}
              onCancel={handleCancelConfirmed}
              onReschedule={handleReschedule}
            />
          )}
        </div>

        {/* Footer premium com link */}
        <p className="text-center text-xs text-muted-foreground/60 mt-8">
          Powered by{' '}
          <a
            href="https://uaizychat.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium transition-colors"
          >
            UaizyChat
          </a>
        </p>
      </div>
    </div>
  );
};

export default PublicScheduling;
